import { useAuthStore } from '@stores/auth'
import postAuthRedirect from '@utils/post-auth-redirect'

// The guards that read a store. Nuxt runs them as route middleware (app/middleware),
// named in each route's `meta.middleware`: a record's own `beforeEnter` would run in
// the router's first navigation, before @pinia/nuxt has created Pinia.

// These return `true` to let a navigation through, as vue-router guards do. Nuxt stops
// running a route's remaining middleware at the first one that returns anything, so
// "let through" has to be no result at all.
export function asRouteMiddleware(guard) {
    const letThrough = result => (result === true ? undefined : result)

    return (to, from) => {
        const result = guard(to, from)
        return result instanceof Promise ? result.then(letThrough) : letThrough(result)
    }
}

// The destination a signed-out visitor is bounced from is remembered by the
// global guard under `redirectURL`, which is what login reads on the way back —
// so this only has to decide whether to let them through.
export function requireAuth() {
    return useAuthStore().loggedIn || { name: 'login' }
}

export function requireAuthOrGoHome() {
    return useAuthStore().loggedIn || { name: 'home' }
}

export function redirectHomeIfLoggedIn() {
    return !useAuthStore().loggedIn || { name: 'home' }
}

// Payment pages are strictly post-auth: a signed-out visitor (old link,
// bookmark, mid-funnel plan pick) is sent to sign-up first and returned to
// this exact payment page — plan id in the path, postAuth marker so the
// payment page can dissolve the funnel if they turn out to be Premium.
export function requireAuthForCheckout(routeTo) {
    if (useAuthStore().loggedIn) return true
    const joiner = routeTo.fullPath.includes('?') ? '&' : '?'
    postAuthRedirect.set(`${routeTo.fullPath}${joiner}postAuth=1`)
    return { name: 'signup' }
}

// /logout has no component. Returning nothing would allow the navigation and blank
// the page for the length of logOut()'s revoke request, so abort explicitly and let
// the reload logOut() performs do the navigating.
export function logOut() {
    useAuthStore().logOut()
    return false
}
